// components/AudioPlayer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { FaPlayCircle, FaPauseCircle, FaVolumeDown, FaVolumeMute } from "react-icons/fa";
import { goldColour, blueColour } from '../pages/Home';
import VisDiv from '../whitaijiFrontendLibrary/components/VisDiv';
import { FaStepForward, FaStepBackward } from 'react-icons/fa';
import useMediaQuery from '../hooks/useMediaQuery';
import MusicPlayerScreen from './MusicPlayerScreen';
import useSongLogger from '../hooks/useSongLogger';
import { CiCircleCheck } from "react-icons/ci";
import AudioBufferLoader from './AudioBufferLoader';
import { useAudioController } from '../hooks/useAudioController';
import BlueButton from './BlueButton';
import AudioProgressBar from './AudioProgressBar';
import { useLocation } from 'react-router-dom';

export const ConcertMusicPlayer = ({
    hideScreen, 
    logPercentage, 
    triggerSongIndexChange, 
    songLogCallback = ()=>{}, 
    hideTestButtons, 
    songsCompleted, 
    songSource: initialSongSource, 
    title: initialTitle, 
    containerStyle ={}, 
    crossFadeSeconds, 
    lyrics: initialLyrics, 
    secondsOnly, 
    songs
  }) => {

  const containerRef = useRef(null);
  return (
        <div ref={containerRef} className="small-music-player" style={{
            padding: '10px',
            marginTop: '10px',
            borderRadius: '10px',
            width: '100%',
            maxWidth: '600px',
            ...containerStyle
          }}>
      <AudioPlayer 
         data = {{
            hideScreen, 
            logPercentage, 
            triggerSongIndexChange, 
            songLogCallback, 
            hideTestButtons, 
            songsCompleted, 
            songSource: initialSongSource, 
            title: initialTitle, 
            containerStyle, 
            crossFadeSeconds, 
            lyrics: initialLyrics, 
            secondsOnly, 
            songs
          }}
        />
    </div>
  )
}

export default ConcertMusicPlayer

export const AudioPlayer = ({data={}}) => {
  const location = useLocation();
  
  const {
    hideScreen, 
    logPercentage, 
    triggerSongIndexChange, 
    songLogCallback = ()=>{}, 
    hideTestButtons, 
    songsCompleted, 
    songSource: initialSongSource, 
    title: initialTitle, 
    containerStyle ={}, 
    crossFadeSeconds, 
    lyrics: initialLyrics, 
    secondsOnly, 
    songs,
    repeatPlaylist
  } = data  
  const screenSizeNotMobile = useMediaQuery("(min-width: 742px)");

    const [currentLyric, setCurrentLyric] = useState('');
    const [currentSongIndex, setCurrentSongIndex] = useState(0);
    
    // Extract isWithPrelude and aiVoSource from current song
    const currentSong = songs?.[currentSongIndex];

    const isWithPrelude = currentSong?.isWithPrelude;
    const aiVoSource = currentSong?.aiVoSource;
    const currentTitle = currentSong?.title;
    const currentSongId = currentSong?._id

    const [currentTime, setCurrentTime] = useState(0);
    const [second, setSecond] = useState(0);
    const duration = parseInt(currentTime?.duration) || currentTime?.duration

      
      const [currentSongSource, setCurrentSongSource] = useState(initialSongSource);
      //const [currentTitle, setCurrentTitle] = useState(initialTitle);
      const [currentLyrics, setCurrentLyrics] = useState(initialLyrics);
      useEffect(() => {
        setCurrentLyric(currentSong?.affirmation);
        setCurrentLyrics(initialLyrics ? initialLyrics : currentSong?.lyrics);
      }, [ initialLyrics, currentSong]);

  const [loading, setLoading] = useState(true);
  
  //const [isPlaying, setIsPlaying] = useState(false);
  //const [isPaused, setIsPaused] = useState(false);

  const audioContextRef = useRef(null);
  const audioBuffersRef = useRef(null);
  const [error, setError] = useState(null);
  const mainTrackVolumeRef = useRef(0.7);
  const voTrackVolumeRef = useRef(0.7);


  const audioController = useAudioController(audioContextRef, audioBuffersRef);
  const { play, isPlaying, pause, isPaused, stop, resume, seek, fadeTo, setVolume, setVoVolume, mute, unmute, setOnFinish, isMuted, cleanupVoiceOver } = audioController

    const { songLogTime, songLogSubmitted } = useSongLogger({
    audioController,
    songId: currentSongId,
    isPlaying,
    minListenTime: (logPercentage && duration)? Math.floor(logPercentage * duration) : 30,
    //minListenTime: 5,
    onLogSuccess: (data) => {
      process.env.NODE_ENV === "development" && console.log('Song logged in ConcertMusicPlayer:', data);
      songLogCallback(data)
    },
    onLogFailed: (data) => {
      process.env.NODE_ENV === "development" && console.log('Song logged failed but still logged in ConcertMusicPlayer:', data);
      songLogCallback(data)
    }
  });

  useEffect(() => {
  setOnFinish((trackId) => {
    console.log('Track finished:', trackId);
    if (repeatPlaylist) {
       handleNext();
    }
    if (!repeatPlaylist) {
      const nextIndex = (currentSongIndex + 1)
      if(nextIndex >= songs.length) {
        return
      }
      handleNext();
    }
   
    // Do something like auto-play next
  });
}, [audioController]);

  // Add event listener to pause audio when user navigates away from the site
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isPlaying) {
        process.env.NODE_ENV === "development" && console.log('Navigating away, stopping audio');
        stop();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPlaying]);

  // Stop audio when component unmounts (which happens when navigating to a different page)
  useEffect(() => {
    // This cleanup function will run when the component unmounts
    return () => {
      // Always stop audio when unmounting, regardless of isPlaying state
      process.env.NODE_ENV === "development" && console.log('Component unmounting, stopping audio');
      stop();
    };
  }, []); // Empty dependency array means this only runs on mount/unmount


  const handleReady = (buffers, context) => {
    audioContextRef.current = context;
    audioBuffersRef.current = buffers;
  };
  const playTrack = (trackId, bypassResume) => {
    if(isPaused && !bypassResume) {
      process.env.NODE_ENV === "development" && console.log('Resuming selected track');
      resume(trackId)
      return
    }
    
    process.env.NODE_ENV === "development" && console.log('Play selected track');
    
    // Get prelude length if available
    const bufferObject = audioBuffersRef.current.get(trackId);
    const lyrics = bufferObject?.lyrics;
    const preludeLength = lyrics?.[0]?.time;
    
    // If we have a prelude, pass it to the play function
    if (preludeLength) {
      play({ 
        trackId, 
        voVolume: voTrackVolumeRef.current, 
        trackVolume: mainTrackVolumeRef.current,
        preludeLength
      });
    } else {
      play({ 
        trackId, 
        voVolume: voTrackVolumeRef.current, 
        trackVolume: mainTrackVolumeRef.current 
      });
    }
  };

  const pauseTrack = () => {
    process.env.NODE_ENV === "development" && console.log('Pause selected track');
    pause();
    //setIsPaused(true);
    //setIsPlaying(false);
  };
/*   const seekTrack = (trackId, time) => {
    seek(trackId, time);
  }; */

// This function will be called by AudioProgressBar when time updates
// We're using useCallback to prevent it from being recreated on every render
const time = React.useCallback((time) => {
  // Just store the time data without any processing here
  // The actual processing happens in the useEffect below
  setCurrentTime(time);
}, []);
const lastLoggedSecondRef = useRef(null);

useEffect(() => {
  if (!isPlaying) return;
  //console.log('currentTime is', currentTime);
  const currentSecond = Math.floor(currentTime?.currentTime);

  if (lastLoggedSecondRef.current !== currentSecond) {
    lastLoggedSecondRef.current = currentSecond;
    setSecond(currentSecond);
    if (process.env.NODE_ENV === 'development') {
      //console.log('time', currentSecond);
    }
     // Update lyrics if available
    if (currentLyrics) {
      const currentLyricObj = currentLyrics?.find((lyric, index) => {
        const nextLyric = currentLyrics[index + 1];
        return currentSecond >= lyric.time && (!nextLyric || currentSecond < nextLyric.time);
      });
      const preludeLength = currentLyrics?.[0]?.time;
      if(preludeLength && currentSecond < preludeLength && isWithPrelude && currentSong?.affirmation) {
        //console.log("ksncxkjscdn", currentSong)
        setCurrentLyric(currentSong?.affirmation);

      }
      else if (currentLyricObj) {
        setCurrentLyric(currentLyricObj.text);
      } else {
        setCurrentLyric('');
      }
    }
  }
}, [currentTime, isPlaying]);

  const fadeToTrack = (trackId) => {
    fadeTo(trackId);
  };

  const handleSongSelect = (index) => {
    // Clean up any existing voice over
    cleanupVoiceOver();
    stop();
    
    // Don't do anything if selecting the current song
    if (index === currentSongIndex) {
      process.env.NODE_ENV === "development" && console.log('Selected current song, no change needed');
      return;
    }
    
    process.env.NODE_ENV === "development" && console.log(`Starting track change to selected track: ${index}`);
    
    // Immediately set the affirmation if available to avoid race condition
    if (songs && songs[index] && songs[index].isWithPrelude && songs[index].affirmation) {
      process.env.NODE_ENV === "development" && console.log('Pre-setting affirmation for selected track');
      setCurrentLyrics(songs[index].lyrics);
      setCurrentLyric(songs[index].affirmation);
    } else {
      // Reset current lyric if no affirmation is available
      setCurrentLyrics(songs[index].lyrics);
      setCurrentLyric('');
    }
    
    // Update the song index 
    setCurrentSongIndex(index);
    
    playTrack(songs[index]._id, true);
  }
  const handlePrevious = () => {
    // Clean up any existing voice over
    cleanupVoiceOver();
    stop();
    
    const prevIndex = (currentSongIndex - 1 + songs.length) % songs.length;
    
    // Immediately set the affirmation if available to avoid race condition
    if (songs[prevIndex] && songs[prevIndex].isWithPrelude && songs[prevIndex].affirmation) {
      process.env.NODE_ENV === "development" && console.log('Pre-setting affirmation for previous track');
      setCurrentLyrics(songs[prevIndex].lyrics);
      setCurrentLyric(songs[prevIndex].affirmation);
    } else {
      // Reset current lyric if no affirmation is available
      setCurrentLyrics(songs[prevIndex].lyrics);
      setCurrentLyric('');
    }
    
    // Update the song index
    setCurrentSongIndex(prevIndex);
    playTrack(songs[prevIndex]._id, true);
  }
  const handleNext = () => {
    // Clean up any existing voice over
    cleanupVoiceOver();
    stop();
    
    const nextIndex = (currentSongIndex + 1) % songs.length;
    
    // Immediately set the affirmation if available to avoid race condition
    if (songs[nextIndex] && songs[nextIndex].isWithPrelude && songs[nextIndex].affirmation) {
      process.env.NODE_ENV === "development" && console.log('Pre-setting affirmation for next track');
      setCurrentLyrics(songs[nextIndex].lyrics);
      setCurrentLyric(songs[nextIndex].affirmation);
    } else {
      // Reset current lyric if no affirmation is available
      setCurrentLyrics(songs[nextIndex].lyrics);
      setCurrentLyric('');
    }
    
    // Update the song index
    setCurrentSongIndex(nextIndex);
    playTrack(songs[nextIndex]._id, true);
  }
  const toggleMute = () => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  };
  const testFunction = () => {
    console.log("testFunction welkdnmlkewdm", {tracks: songs, audioBuffersRef, currentLyric, currentLyrics, currentSong, currentSongIndex, duration, second})
  }

  return (
    <div>
      <AudioBufferLoader
        tracks={songs}
        onReady={handleReady}
        loading={loading}
        setLoading={setLoading}
      />
      {!loading && (
      <div>
          <BlueButton fn={testFunction} hide={hideTestButtons} />
         {/* <button onClick={() => fadeTo('voiceTrackId')}>🎚️ Fade to voiceover</button>*/}

      <VisDiv style={{marginBottom: '10px'}} rules={[currentTitle]}>
       <h3>{currentTitle}</h3>
      </VisDiv>

      {error && (
        <div style={{
          marginBottom: '10px',
          width: '100%',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: '5px',
          color: '#666'
        }}>
          {error}
        </div>
      )}
            {/* Show Screen */}
      <MusicPlayerScreen 
        hideScreen={hideScreen} 
        songs={songs} 
        title={currentTitle} 
        currentLyric={currentLyric} 
        currentLyrics={currentLyrics} 
        isPlaying={isPlaying}/>
      {/* Controls */}

        <AudioProgressBar
          audioContextRef={audioContextRef}
          audioBuffersRef={audioBuffersRef}
          audioController={audioController}
          currentTrackId={currentSongId}
          timeCallback={time}
        />
      <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center', justifyContent: 'center', gap: '10px', flexDirection: !screenSizeNotMobile ? 'column' : 'row' }}>
        <div style={{flex:1}}></div>
        <div style={{flex:2, display:"flex", justifyContent:'center', flexDirection:'row'}}>
        {/* Playlist Controls */}
          <VisDiv rules={[songs && songs.length > 1]} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px',
          }}>
              <button onClick={handlePrevious} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer',}}>
              <FaStepBackward color={blueColour} size={30} />
            </button>
          </VisDiv>
          <button
            onClick={()=>isPlaying ? pauseTrack() : playTrack(currentSongId)} style={{backgroundColor: 'transparent', border: 'none', padding: '10px 20px', cursor: 'pointer', fontSize: '16px',}}>
            {isPlaying ? <FaPauseCircle color={goldColour} size={75}/> : <FaPlayCircle color={blueColour} size={75}/>}
          </button>
          <VisDiv rules={[songs && songs.length > 1]} style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '20px',
          }}>
            <button
              onClick={handleNext}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <FaStepForward color={blueColour} size={30} />
            </button>
          </VisDiv>
        </div>
        <div style={{flex:1, display: 'flex',  alignItems: 'center', }}>
          <div>
            <div style={{display: 'flex', padding: '5px', alignItems: 'center', justifyContent: 'space-between'}}>
              <label>Volume: </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                defaultValue= {mainTrackVolumeRef.current}
                onChange={(e) => {
                  mainTrackVolumeRef.current = parseFloat(e.target.value)
                  setVolume(currentSongId, parseFloat(e.target.value))
                }
                }
                className="volume-slider"
              />
            </div>

            <VisDiv rules={[currentSong?.aiVoSource]} style={{display: 'flex', padding: '5px', alignItems: 'center', justifyContent: 'space-between' }}>
              <label>Voice: </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                defaultValue={voTrackVolumeRef.current}
                className="volume-slider"
                onChange={(e) => {
                  voTrackVolumeRef.current = parseFloat(e.target.value)
                  setVoVolume(currentSongId, parseFloat(e.target.value))
                }}
              />
            </VisDiv>
          </div>
          <div style={{ height: '100%'}}>
            <button
              onClick={toggleMute}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
            {isMuted ? <FaVolumeMute color={blueColour} size={25}/> : <FaVolumeDown color={goldColour} size={25}/> }
          </button>
          </div>
        </div>
      </div>
           {/* Show songlist if tracks array length > 1 */}
      {songs && songs.length > 1 && (
        <div style={{
          padding: '5px',
          //maxHeight: '200px',
          //overflowY: 'auto',
          marginTop: '20px'
        }}>
          {songs?.map((song, index) => { 
            const isCompleted = songsCompleted?.some((x)=>(x?.songId == song?._id))
            return(
            <div
              key={index}
              onClick={() => handleSongSelect(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                cursor: 'pointer',
                backgroundColor: currentSongIndex === index ? `${goldColour}20` : 'transparent',
                borderRadius: '5px',
                marginBottom: '5px',
                transition: 'background-color 0.3s',
                color: currentSongIndex === index ? goldColour : blueColour,
                fontWeight: currentSongIndex === index ? 'bold' : 'normal',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentSongIndex === index ? `${goldColour}30` : `${blueColour}10`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = currentSongIndex === index ? `${goldColour}20` : 'transparent';
              }}
            >
              <VisDiv style={{marginRight:"10px"}} className="flex-center" rules={[isCompleted]}>
                  <CiCircleCheck size={24} color={goldColour} />
              </VisDiv>
              {song.title}
            </div>
          )})}
        </div>
      )}
    </div>

      )}
    </div>
  );
}
